import { useContext, useMemo, useRef } from "react";
import { styled } from "@mui/material";
import { Context } from "../../Provider";
import { formatTimestampInMap } from "../../../../utils";
import { theme } from "../../../../theme";
import { uploadPackageImage } from "../../service";
import toast from "react-hot-toast";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

const TrackerSettings = () => {
    const fileInputRef = useRef(null);
    const {
        state: { ftm },
        dispatch,
        setupDevice,
        removeDevice,
        setupSimNumber,
        removeSimNumber,
        ftmTrackerActiveAndReceivingLocations,
        ftmTrackerDispatch,
        trackerInstalled,
    } = useContext(Context);

    const {
        ftm_id,
        ftm_tracker_sim_number,
        ftm_device_id,
        ftm_device_timestamp,
        ftm_enable_gps_external_device,
        ftm_tracker_is_install,
        ftm_tracker_installed_timestamp,
        ftm_gps_tracker_is_dispatch,
        ftm_tracker_dispatch_timestamp,
        ftm_package_image_url,
        ftm_is_gps_fixed_and_receiving_locations,
    } = ftm;

    const triggerFileUpload = () => fileInputRef.current?.click();

    const handleFileSelect = async (files) => {
        if (!files?.length) return;
        try {
            const res = await uploadPackageImage({ ftm_id, package_image: files[0] });
            res.status === "Success" ? toast.success(res.message) : toast.error(res.message);
            dispatch({ type: "SET_FTM_EDIT_COUNT" });
        } catch (err) {
            console.error("Upload failed:", err);
            toast.error("Upload failed!");
        }
    };

    const settings = useMemo(
        () => [
            {
                icon: "sim_card",
                name: ftm_tracker_sim_number || "Tracking SIM Number",
                text: "Phone number of the SIM card used in the GPS tracker.",
                buttons: [
                    { icon: "edit", action: setupSimNumber, color: theme.close_btn_code, isDisabled: !!ftm_tracker_sim_number, show: true },
                    { icon: "delete", action: removeSimNumber, color: theme.delete_btn_code, isDisabled: !ftm_tracker_sim_number, show: true },
                ],
                isComplete: !!ftm_tracker_sim_number,
            },
            {
                icon: "satellite_alt",
                name: ftm_device_id || "GPS Device ID Not Set",
                text: ftm_device_timestamp
                    ? `GPS tracker registered on ${formatTimestampInMap(ftm_device_timestamp, true)}.`
                    : "No GPS device ID has been registered.",
                buttons: [
                    { icon: "edit", action: setupDevice, color: theme.close_btn_code, isDisabled: ftm_enable_gps_external_device === 1, show: true },
                    { icon: "delete", action: removeDevice, color: theme.delete_btn_code, isDisabled: ftm_enable_gps_external_device === 0, show: true },
                ],
                isComplete: ftm_enable_gps_external_device === 1,
            },
            {
                icon: "settings_b_roll",
                name: "Tracker Installation",
                text: ftm_tracker_is_install
                    ? `GPS tracker installed on ${formatTimestampInMap(ftm_tracker_installed_timestamp, true)}.`
                    : "GPS tracker has not been installed for this tradesperson.",
                buttons: [
                    { icon: "send", action: trackerInstalled, color: theme.close_btn_code, isDisabled: ftm_tracker_is_install === 1, show: ftm_tracker_is_install !== 1 },
                ],
                isComplete: ftm_tracker_is_install === 1,
            },
            {
                icon: "moped_package",
                name: "Tracker Dispatch",
                text: ftm_tracker_dispatch_timestamp
                    ? `GPS tracker dispatched on ${formatTimestampInMap(ftm_tracker_dispatch_timestamp, true)}.`
                    : "GPS tracker has not been dispatched to the tradesperson.",
                buttons: [
                    { 
                        icon: "send", 
                        action: ftmTrackerDispatch, 
                        color: theme.close_btn_code, 
                        isDisabled: ftm_gps_tracker_is_dispatch === 1, 
                        show: ftm_gps_tracker_is_dispatch !== 1 
                    },
                ],
                isComplete: ftm_gps_tracker_is_dispatch === 1,
            },
            {
                icon: "archive",
                name: "Package Photo",
                text: "Upload a photo of the package sent to the tradesperson.",
                buttons: [
                    ...(ftm_package_image_url
                        ? [{ 
                                label: "View Photo", 
                                icon: "photo", action: () => window.open(ftm_package_image_url, "_blank"), 
                                color: theme.close_btn_code, 
                                isDisabled: false,
                                show: true
                            }]
                        : []),
                    {
                        label: "Upload Photo",
                        icon: "upload",
                        action: triggerFileUpload,
                        color: !ftm_package_image_url ? theme.main : theme.close_btn,
                        isDisabled: false,
                        show: true
                    },
                ],
                isComplete: Boolean(ftm_package_image_url),
            },
            {
                icon: "location_on",
                name: `Tracking Location ${ftm_is_gps_fixed_and_receiving_locations ? "Active" : "Inactive"}`,
                text: ftm_is_gps_fixed_and_receiving_locations
                    ? "GPS tracker is actively sending location data."
                    : "GPS tracker is not currently sending location data.",
                buttons: [
                    {
                        icon: "send",
                        action: ftmTrackerActiveAndReceivingLocations,
                        color: theme.close_btn_code,
                        isDisabled: ftm_is_gps_fixed_and_receiving_locations === 1,
                        show: ftm_is_gps_fixed_and_receiving_locations !== 1
                    },
                ],
                isComplete: ftm_is_gps_fixed_and_receiving_locations === 1,
            },
        ],
        [
            ftm_tracker_sim_number,
            ftm_device_id,
            ftm_device_timestamp,
            ftm_enable_gps_external_device,
            ftm_tracker_is_install,
            ftm_tracker_installed_timestamp,
            ftm_gps_tracker_is_dispatch,
            ftm_tracker_dispatch_timestamp,
            ftm_package_image_url,
            ftm_is_gps_fixed_and_receiving_locations,
            setupSimNumber,
            removeSimNumber,
            setupDevice,
            removeDevice,
            trackerInstalled,
            ftmTrackerDispatch,
            ftmTrackerActiveAndReceivingLocations,
        ]
    );

    return (
        <div className="row mt-4">
            {settings.map((item, index) => (
                <div className="col-lg-2 col-sm-6" key={index}>
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex mb-2">
                                {item.isComplete && <i className="material-icons mb-0 text-success">verified</i>}
                                <ul className="navbar-nav ms-auto d-flex flex-row align-items-center">
                                    {item.buttons?.map((button, btnIndex) => (
                                        <li className="nav-item" key={btnIndex}>
                                            <i
                                                className={`material-icons cursor-pointer ${item.buttons.length > 1 && btnIndex === 0 ? "me-2" : ""}`}
                                                onClick={button.action}
                                                style={{ color: button.color, opacity: button.isDisabled ? 0.5 : 1, display: "node" }}
                                            >
                                                {button.icon}
                                            </i>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <i className="material-icons opacity-10" style={{ fontSize: 70 }}>
                                {item.icon}
                            </i>
                            <p className="mt-0 mb-0 font-weight-bold">{item.name}</p>
                            <span className="text-xs font-weight-bold">{item.text}</span>
                        </div>
                    </div>
                </div>
            ))}

            <VisuallyHiddenInput
                type="file"
                ref={fileInputRef}
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={(e) => handleFileSelect(e.target.files)}
            />
        </div>
    );
};

export default TrackerSettings;